import { Chart as ChartJS, registerables } from "chart.js";
import { Doughnut, Line } from "react-chartjs-2";
import { useAnalyticsStateContext, useAnalyticsDispatchContext } from "../../../context/AnalyticsContext";
import "./AnalyticsGraph.css";
import BuildMetricsChoice from "../../../utils/BuildMetricsChoice";
import { useState, useRef } from "react";
import IconButton from "/src/components/UI/Button/IconButton/IconButton";
import { AnalyticsGraphDataFactory, PrepareDataForDownload } from "./AnalyticsGraphDataFactory";
import AnalyticsGraphHook from "./AnalyticsGraphHook";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { ThreeDots } from "react-loader-spinner";

import PeakActivityGraph from "./PeakActivityGraph";
import RedirectionActivityPieGraph from "./RedirectionActivityPieGraph";
import RedirectionActivityGraph from "./RedirectionActivityGraph";


ChartJS.register(...registerables);

function AnalyticsGraph({ option }) {
    const { analytics, metrics, metrics_id, isLoading, view, peakActivity, redirectionActivity} = useAnalyticsStateContext();
    const dispatch = useAnalyticsDispatchContext();

    const [chart, setChart] = useState("pie");
    const canvasRef = useRef(null);
    const [clickedLabel, setClickedLabel] = useState(null);
    const [clickedTime, setClickedTime] = useState(null);
    const [hoveredLabelIndex, setHoveredLabelIndex] = useState(null);
    // const { dateLabel , timeLabel  } = useAnalyticsStateContext();

    var type = option;

    const metricsChoice = BuildMetricsChoice(metrics);

    const { setChoiceMetric, getMetricValue } = AnalyticsGraphHook(metrics, metrics_id);

    const metricValue = getMetricValue();

    if (option === "metrics" && metrics_id != null && metricValue == null) {
        return null;
    }

    const config = AnalyticsGraphDataFactory(analytics, metrics, option, chart, metrics_id);

    const options =
        chart == "line"
            ? {
                  scales: {
                      xAxis: {
                          ticks: {
                              maxTicksLimit: 10,
                              color: function (context) {
                                  return context.index === hoveredLabelIndex ? "#0e7aed" : "black";
                              },
                              font: function (context) {
                                  return context.index === hoveredLabelIndex ? { weight: "bold" } : {};
                              },
                          },
                      },
                      yAxis: {
                          min: 0,
                          max: config.max,
                          ticks: {
                              maxTicksLimit: 6,
                              beginAtZero: true,
                          },
                      },
                  },
              }
            : {
                  plugins: {
                      tooltip: {
                          callbacks: {
                              label: function (info) {
                                  const { label, dataset, dataIndex, chart, datasetIndex } = info;

                                  if (Array.isArray(dataset.data)) {
                                      const data = dataset.data.filter(
                                          (value, index) => chart._hiddenIndices[index] !== true
                                      );
                                      let total = data.reduce(
                                          (previousValue, currentValue) => previousValue + currentValue,
                                          0
                                      );
                                      let currentValue = dataset.data[dataIndex];
                                      let percentage = Math.round((currentValue / total) * 100 * 10) / 10;
                                      return `${label} : ${percentage} % (${currentValue})`;
                                  } else {
                                      const keys = Object.keys(dataset.data);
                                      let currentValue;
                                      if (keys.length > dataIndex) {
                                          const key = keys[dataIndex];
                                          currentValue = dataset.data[key];
                                      } else {
                                          currentValue = "N/A";
                                      }
                                      return `${label} : ${currentValue}`;
                                  }
                              },
                          },
                      },
                  },
              };
    const plugins = [
        {
            afterDraw: (chart) => {
                let meta = chart.getDatasetMeta(0);
                if (meta.type == "doughnut") {
                    let data = meta._dataset.data.filter((value, index) => chart._hiddenIndices[index] !== true);
                    if (data.length !== 0) return;
                }

                if (meta.type == "line") {
                    let data = chart.data.datasets.filter(
                        (dataset) => chart.getDatasetMeta(dataset.id).hidden !== true
                    );
                    if (data.length !== 0) return;
                }

                let ctx = chart.ctx;
                let width = chart.width;
                let height = chart.height;
                ctx.textAlign = "center";
                ctx.textBaseline = "middle";
                ctx.font = "30px Arial";
                // ctx.fillText("No data to display", width / 2, height / 2);
                ctx.restore();
            },
        },
    ];

    // To refactor if the client ask for multiple download zzzzz
    const downloadAnalyticsXlxs = () => {
        const { values: data, name } = PrepareDataForDownload(analytics, metrics, type, option, metrics_id);

        const workbook = XLSX.utils.book_new();
        const sheet = XLSX.utils.aoa_to_sheet([[]]);

        let allDates = [];
        for (const prop in data) {
            for (const date in data[prop]) {
                if (allDates.includes(date)) continue;
                allDates.push(date);
            }
        }

        allDates.sort((a, b) => new Date(b) - new Date(a));

        const headerRow = ["Date"];
        for (const prop in data) {
            headerRow.push(prop);
        }
        XLSX.utils.sheet_add_aoa(sheet, [headerRow]);

        // Ajouter les données
        allDates.forEach((date) => {
            const rowData = [date];
            for (const prop in data) {
                rowData.push(data[prop][date] || 0);
            }
            XLSX.utils.sheet_add_aoa(sheet, [rowData], { origin: -1 });
        });

        XLSX.utils.book_append_sheet(workbook, sheet, "Metric");
        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
        const nameFile = +Date.now() + "_" + (name ? name : id);
        saveAs(blob, `${nameFile}.xlsx`);
    };

    // Cette fonction me permet de gerer les clics sur le canvas
    const handleCanvasClick = (event) => {
        const chart = ChartJS.getChart(canvasRef.current);

        if (view !== "dates" && view !== "hours") {
            return;
        }

        if (!chart) return;

        // Récupération de la position du clic par rapport au canvas
        const canvasPosition = chart.canvas.getBoundingClientRect();
        const x = event.clientX - canvasPosition.left;
        const y = event.clientY - canvasPosition.top;

        // Récupération des axes X et Y du chart
        const xAxis = chart.scales.x || chart.scales.xAxis;
        const yAxis = chart.scales.y || chart.scales.yAxis;

        if (!xAxis || !yAxis) {
            console.error("Axes introuvables");
            return;
        }

        // Conversion des coordonnées du clic en valeurs de données sur les axes
        const dataX = xAxis.getValueForPixel(x);
        const dataY = yAxis.getValueForPixel(y);

        // Calcul de l'index du point cliqué dans le tableau des labels
        const index = Math.round(dataX);

        // Si l'index est valide, on récupère le label correspondant et on le traite
        if (index >= 0 && index < chart.data.labels.length) {
            const clickedLabel = chart.data.labels[index];
            handlePointClick(clickedLabel);
        }
    };

    function isValidDateString(dateString) {
        const regex = /^\d{4}-\d{2}-\d{2}$/;
        if (!regex.test(dateString)) {
            return false;
        }

        const date = new Date(dateString);

        if (isNaN(date.getTime())) {
            return false;
        }

        const [year, month, day] = dateString.split("-");
        return (
            date.getUTCFullYear() === parseInt(year, 10) &&
            date.getUTCMonth() + 1 === parseInt(month, 10) &&
            date.getUTCDate() === parseInt(day, 10)
        );
    }

    const handlePointClick = (label) => {
        if (view === "dates") {
            setClickedLabel(label);
            dispatch({ type: "SET_CLICKED_LABEL", payload: label });
            dispatch({ type: "SET_VIEW", payload: "hours" });
        } else if (view === "hours") {
            if (isValidDateString(label)) {
                setClickedLabel(label);
                dispatch({ type: "SET_CLICKED_LABEL", payload: label });
                dispatch({ type: "SET_VIEW", payload: "hours" });
            } else {
                setClickedTime(label);
                dispatch({ type: "SET_CLICKED_TIME", payload: { date: clickedLabel, time: label } });
                dispatch({ type: "SET_VIEW", payload: "other" });
            }
        }
    };

    const getSortedAndFormattedDataForAllDatasets = (datasets, id) => {
        const sortedKeys = Object.keys(datasets[0].data).sort((a, b) => a - b);
        const formattedLabels = sortedKeys.map((key) => {
            if (clickedTime) {
                let hour = clickedTime?.split(":")[0];
                return `${hour}h${key}`;
            } else {
                return clickedLabel;
            }
        });

        const formattedDatasets = datasets.map((dataset) => {
            const sortedData = sortedKeys.map((key) => dataset.data[key]);
            return {
                ...dataset,
                data: sortedData,
            };
        });

        return {
            id,
            labels: formattedLabels,
            datasets: formattedDatasets,
        };
    };

    const handleMouseMove = (event) => {
        const chart = ChartJS.getChart(canvasRef.current);
        if (!chart) return;

        const canvas = chart.ctx.canvas;
        const canvasPosition = canvas.getBoundingClientRect();
        const x = event.clientX - canvasPosition.left;

        const xAxis = chart.scales.x || chart.scales.xAxis;
        const ticks = xAxis.ticks.map((tick) => xAxis.getPixelForValue(tick.value));
        const tickWidth = 10;

        const hoveredIndex = ticks.findIndex((tickX) => x >= tickX - tickWidth / 2 && x <= tickX + tickWidth / 2);

        if (hoveredIndex >= 0 && view != "other") {
            setHoveredLabelIndex(hoveredIndex);
            canvas.style.cursor = "pointer";
        } else {
            setHoveredLabelIndex(null);
            canvas.style.cursor = "default";
        }
    };

    const sortedConfig =
        view === "other" ? getSortedAndFormattedDataForAllDatasets(config.datasets, config.id) : config;

    const handleArrowClick = (direction) => {
        dispatch({ type: "SET_VIEW", payload: "dates" });
        setClickedLabel(null);
        setClickedTime(null);
        dispatch({ type: "SET_CLICKED_LABEL", payload: null });
        dispatch({ type: "SET_CLICKED_TIME", payload: { time: null, date: null } });

        setChoiceMetric(direction);
    };

    return (
        <div className="chart_container">
            {isLoading ? (
                <div className="loader-container">
                    <ThreeDots height="100" width="100" color="var(--main)" ariaLabel="loading" />
                </div>
            ) : (
                <div style={{ display: "flex", flexDirection: "column" }}>
                    {view !== "dates" && metrics_id === null && (
                        <div className="back-button">
                            <IconButton
                                className="main-iconButton profil_action main_add-button space_tableButton"
                                icon="fas fa-arrow-left"
                                onClick={() => {
                                    dispatch({ type: "SET_VIEW", payload: "dates" });
                                    setClickedLabel(null);
                                    setClickedTime(null);
                                    dispatch({ type: "SET_CLICKED_LABEL", payload: null });
                                    dispatch({ type: "SET_CLICKED_TIME", payload: { time: null, date: null } });
                                }}
                            />
                        </div>
                    )}

                    {type === "metrics" && metrics_id != null ? (
                        <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between" }}>
                            <div>
                                <IconButton
                                    className={
                                        "main-iconButton profil_action main_add-button space_tableButton" +
                                        (chart === "line" ? " selected" : "")
                                    }
                                    icon="fas fa-chart-line"
                                    onClick={() => setChart("line")}
                                />
                                <IconButton
                                    className={
                                        "main-iconButton profil_action main_add-button space_tableButton" +
                                        (chart === "pie" ? " selected" : "")
                                    }
                                    icon="fas fa-chart-pie"
                                    onClick={() => setChart("pie")}
                                />
                            </div>
                            <div>
                                <IconButton
                                    className={"main-iconButton profil_action main_add-button space_tableButton"}
                                    icon="fas fa-download"
                                    onClick={downloadAnalyticsXlxs}
                                />
                            </div>
                        </div>
                    ) : null}

                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                        {metricsChoice.length !== 1 ? (
                            <span className="arrow prev" onClick={() => handleArrowClick(-1)}></span>
                        ) : null}
                        <span style={{ fontSize: "22px", fontWeight: "bold" }}>
                            {metrics_id === null
                                ? "All metrics"
                                : getMetricValue(metricsChoice, metrics_id) || "Unknown metric"}
                        </span>
                        {metricsChoice.length !== 1 ? (
                            <div style={{ display: "flex" }}>
                                <span className="arrow next" onClick={() => handleArrowClick(1)}></span>{" "}
                                {view !== "dates" && getMetricValue() === undefined ? (
                                    <span style={{ fontWeight: "bold" }}></span>
                                ) : null}
                            </div>
                        ) : null}
                    </div>
                    {type === "metrics" && metrics_id != null && chart === "pie" ? (
                        <div style={{ width: "50%", margin: "auto" }}>
                            <Doughnut datasetIdKey="pie" data={config} options={options} plugins={plugins} />
                        </div>
                    ) : metrics_id != null && type === "metrics" ? (
                        <Line datasetIdKey="multi-line" data={config} options={options} plugins={plugins} />
                    ) : (
                        <Line
                            ref={canvasRef}
                            datasetIdKey="line"
                            data={sortedConfig}
                            options={options}
                            plugins={plugins}
                            onMouseMove={handleMouseMove}
                            onClick={handleCanvasClick}
                        />
                    )}

                        <RedirectionActivityPieGraph data={redirectionActivity} options={options} plugins={plugins} />
                        <PeakActivityGraph data={peakActivity} options={options} plugins={plugins} />
                        <RedirectionActivityGraph data={redirectionActivity} options={options} plugins={plugins} />
                </div>
            )}
        </div>
    );
}

export default AnalyticsGraph;
